'use client';

import { useState } from 'react';

interface BlogFeedbackProps {
  language: 'en' | 'zh';
  slug: string;
}

export default function BlogFeedback({ language, slug }: BlogFeedbackProps) {
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);

  const handleFeedback = (type: 'up' | 'down') => {
    setFeedback(type);
    // In the future, this could send analytics data
    console.log(`Blog feedback for ${slug}: ${type}`);
  };

  const text = {
    question:
      language === 'en' ? 'Was this article helpful?' : '這篇文章有幫助嗎？',
    thanks: language === 'en' ? 'Thanks for your feedback!' : '感謝您的反饋！',
  };

  return (
    <div className="flex flex-col items-center py-8">
      {/* Decorative divider */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-16 h-px bg-gray-300 dark:bg-gray-600" />
        <span className="text-gray-400 dark:text-gray-500">◆</span>
        <div className="w-16 h-px bg-gray-300 dark:bg-gray-600" />
      </div>

      {/* Feedback section - fixed height to prevent layout shift */}
      <div className="text-center">
        <p
          className="text-lg mb-4"
          style={{ color: 'var(--color-foreground-secondary)' }}
        >
          {feedback ? text.thanks : text.question}
        </p>
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={() => handleFeedback('up')}
            disabled={feedback !== null}
            className={`flex items-center gap-2 px-6 py-3 rounded-full border-2 transition-all duration-200 ${
              feedback === 'up'
                ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                : feedback === 'down'
                  ? 'border-gray-200 dark:border-gray-700 opacity-40'
                  : 'border-gray-200 dark:border-gray-700 hover:border-green-500 dark:hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20'
            }`}
            aria-label={language === 'en' ? 'Yes, helpful' : '是的，有幫助'}
          >
            <svg
              className={`w-6 h-6 transition-colors ${
                feedback === 'up'
                  ? 'text-green-500'
                  : feedback === 'down'
                    ? 'text-gray-400'
                    : 'text-gray-500 group-hover:text-green-500'
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
              />
            </svg>
            <span
              className={`font-medium transition-colors ${
                feedback === 'up'
                  ? 'text-green-600 dark:text-green-400'
                  : feedback === 'down'
                    ? 'text-gray-400'
                    : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              {language === 'en' ? 'Yes' : '是'}
            </span>
          </button>

          <button
            onClick={() => handleFeedback('down')}
            disabled={feedback !== null}
            className={`flex items-center gap-2 px-6 py-3 rounded-full border-2 transition-all duration-200 ${
              feedback === 'down'
                ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                : feedback === 'up'
                  ? 'border-gray-200 dark:border-gray-700 opacity-40'
                  : 'border-gray-200 dark:border-gray-700 hover:border-red-500 dark:hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
            }`}
            aria-label={language === 'en' ? 'No, not helpful' : '不，沒有幫助'}
          >
            <svg
              className={`w-6 h-6 transition-colors ${
                feedback === 'down'
                  ? 'text-red-500'
                  : feedback === 'up'
                    ? 'text-gray-400'
                    : 'text-gray-500 group-hover:text-red-500'
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018c.163 0 .326.02.485.06L17 4m-7 10v2a2 2 0 002 2h.095c.5 0 .905-.405.905-.905 0-.714.211-1.412.608-2.006L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5"
              />
            </svg>
            <span
              className={`font-medium transition-colors ${
                feedback === 'down'
                  ? 'text-red-600 dark:text-red-400'
                  : feedback === 'up'
                    ? 'text-gray-400'
                    : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              {language === 'en' ? 'No' : '否'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
